import React from 'react';
import {
  ActivityIndicator,
  Keyboard,
  Pressable,
  TextInput as RNTextInput,
  View,
} from 'react-native';
import { KeyboardController } from 'react-native-keyboard-controller';

import { SvgIcon, TextInput } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { createStyles, useScaling } from '@/hooks';
import { useDebounce } from '@/hooks/use-debounce';
import { colors } from '@/theme/colors';

/** Wait this long after the guardian stops typing before geocoding the query. */
const SEARCH_DEBOUNCE_MS = 1000;

/** The address / zip-code search field that floats over the top of the map. Keeps
 *  its own text state so each keystroke re-renders only this bar, not the map. Emits the
 *  (debounced) query up via `onSearch` so the parent can geocode it and recentre the
 *  search around that place instead of the guardian's GPS location. */
export const MapSearchBar = React.memo(function MapSearchBar({
  onSearch,
  loading,
}: {
  onSearch?: (text: string) => void;
  /** Show a spinner in place of the trailing edge while the parent geocodes the query. */
  loading?: boolean;
}) {
  const styles = useStyles();
  const { scale } = useScaling();
  const [query, setQuery] = React.useState('');
  const inputRef = React.useRef<RNTextInput>(null);

  // `KeyboardController.dismiss()` alone doesn't dismiss the IME on Samsung DeX / freeform Android
  // windows because the inset controller can't always reach the IME bound to the freeform window.
  // Blurring the focused TextInput is the most universal way: removing focus releases the IME
  // target, after which the soft keyboard hides naturally on every platform/mode. Keep
  // `Keyboard.dismiss()` + `KeyboardController.dismiss()` as fallbacks for legacy + Reanimated paths.
  const dismissKeyboard = React.useCallback(() => {
    inputRef.current?.blur();
    Keyboard.dismiss();
    KeyboardController.dismiss();
  }, []);

  // Only fire once typing settles, so we don't geocode every keystroke. Dismiss the keyboard
  // when the search actually runs so the results/map aren't hidden behind the IME.
  const emitSearch = useDebounce((text: string) => {
    onSearch?.(text.trim());
    dismissKeyboard();
  }, SEARCH_DEBOUNCE_MS);

  const handleChangeText = React.useCallback(
    (text: string) => {
      setQuery(text);
      emitSearch(text);
    },
    [emitSearch],
  );

  // Submitting flushes the pending debounce and searches immediately.
  const handleSubmit = React.useCallback(() => {
    emitSearch.cancel();
    onSearch?.(query.trim());
    dismissKeyboard();
  }, [emitSearch, onSearch, query, dismissKeyboard]);

  // Clear the field and drop the search override so the map falls back to GPS immediately.
  const handleClear = React.useCallback(() => {
    setQuery('');
    emitSearch.cancel();
    onSearch?.('');
    dismissKeyboard();
  }, [emitSearch, onSearch, dismissKeyboard]);

  return (
    <View style={styles.searchOverlay} pointerEvents="box-none">
      <TextInput
        value={query}
        onChangeText={handleChangeText}
        status="default"
        onSubmitEditing={handleSubmit}>
        <TextInput.Content style={styles.searchContent}>
          <TextInput.Icon
            position="left"
            name="IcLocation1"
            size={scale(20)}
            color={colors['6']}
            styleWrapper={{
              marginRight: scale(4),
            }}
          />
          <TextInput.Field
            ref={inputRef}
            placeholder={STRINGS.onboarding.step3.mapSearchPlaceholder}
            variant="Font Guardian App/Subtitle/Subtitle 14 Regular"
            autoCapitalize="none"
            autoCorrect={false}
            maxLength={100}
            returnKeyType="search"
          />
          {loading ? (
            <ActivityIndicator size="small" color={colors['6']} style={styles.spinner} />
          ) : query.length > 0 ? (
            <Pressable
              onPress={handleClear}
              hitSlop={8}
              accessibilityRole="button"
              accessibilityLabel="Clear search"
              style={styles.clearButton}>
              <SvgIcon name="IcXCloseCircleFilled" size={scale(18)} color={colors['5']} />
            </Pressable>
          ) : null}
        </TextInput.Content>
      </TextInput>
    </View>
  );
});

const useStyles = createStyles(({ scale }) => ({
  searchOverlay: {
    position: 'absolute',
    top: scale(12),
    left: scale(12),
    width: '60%',
  },
  spinner: {
    marginLeft: scale(4),
  },
  clearButton: {
    marginLeft: scale(4),
  },
  searchContent: {
    paddingLeft: scale(8),
    paddingRight: scale(8),
    borderRadius: scale(10),
    borderColor: colors['0'],
    backgroundColor: colors['0'],
    shadowColor: colors['Grays/Black'],
    shadowOffset: { width: 0, height: scale(2) },
    shadowOpacity: 0.12,
    shadowRadius: scale(8),
    elevation: 4,
  },
}));
